import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { entity_id, Platform } from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";
import { baseapi } from "../Environmentalurl";

const TPIN_MANAGEMENT_URL = `${baseapi}/auth/tpin`;
const STATIC_GEOLOCATION = "17.385044,78.486671";

const errorToastStyle = { background: "red", color: "white", fontWeight: "bold", borderRadius: "8px" };
const successToastStyle = { background: "green", color: "white", fontWeight: "bold", borderRadius: "8px" };

const pinFieldSx = {
    display: "flex",
    justifyContent: "center",
    gap: 1.5,
    mb: 3,
};

export default function CreateTpinSetup({ mobileNumber, geo, onSuccess, onBack }) {
    const ip = useIPAddress();
    const [newTpin, setNewTpin] = useState(["", "", "", ""]);
    const [confirmTpin, setConfirmTpin] = useState(["", "", "", ""]);
    const [saving, setSaving] = useState(false);

    const makeChange = (arr, setter, prefix) => (e, index) => {
        const value = e.target.value;
        if (!/^\d?$/.test(value)) return;
        const updated = [...arr];
        updated[index] = value;
        setter(updated);
        if (value && index < 3) {
            document.getElementById(`${prefix}-${index + 1}`)?.focus();
        }
    };

    const makeKeyDown = (arr, prefix) => (e, index) => {
        if (e.key === "Backspace" && !arr[index] && index > 0) {
            document.getElementById(`${prefix}-${index - 1}`)?.focus();
        }
    };

    const isDisabled =
        saving || newTpin.join("").length !== 4 || confirmTpin.join("").length !== 4;

    const handleCreate = async (e) => {
        e.preventDefault();
        if (saving) return;

        const newStr = newTpin.join("");
        const confStr = confirmTpin.join("");

        if (newStr.length !== 4) {
            toast.error("Please enter a 4-digit TPIN.", { style: errorToastStyle, position: "top-center" });
            return;
        }
        if (newStr !== confStr) {
            toast.error("TPINs do not match. Please try again.", { style: errorToastStyle, position: "top-center" });
            setConfirmTpin(["", "", "", ""]);
            document.getElementById("setup-confirm-0")?.focus();
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ReqType: "MT_TPIN_Management",
                Mobilenumber: String(mobileNumber),          
                Statement: "AddTPIN",
                TPIN: newStr,                                
                OldTPIN: "",
                IsTPINEnabled: "True",
                Platform: Platform,
                EntityID: entity_id,
                GeoLocation: "12.8679936,77.611008",
                MachineID: ip,
                SessionID: "",  // ← added
            };

            const response = await axios.post(TPIN_MANAGEMENT_URL, payload);

            if (String(response.data.Response_Code) === "1710") {
                toast.success(response.data.Message || "TPIN created successfully!", {
                    style: successToastStyle,
                    position: "top-center",
                });
                onSuccess?.();
            } else {
                toast.error(response.data.Message || "Failed to set TPIN.", {
                    style: errorToastStyle,
                    position: "top-center",
                });
            }
        } catch {
            toast.error("Error setting TPIN. Please try again.", { style: errorToastStyle, position: "top-center" });
        } finally {
            setSaving(false);
        }
    };

    const renderRow = (arr, setter, prefix) => (
        <Box sx={pinFieldSx}>
            {arr.map((digit, index) => (
                <input
                    key={index}
                    id={`${prefix}-${index}`}
                    type="password"
                    value={digit}
                    maxLength={1}
                    autoComplete="off"
                    onChange={(e) => makeChange(arr, setter, prefix)(e, index)}
                    onKeyDown={(e) => makeKeyDown(arr, prefix)(e, index)}
                    style={{
                        width: 50,
                        height: 50,
                        textAlign: "center",
                        fontSize: 22,
                        fontWeight: 700,
                        border: "1px solid #d1d5db",
                        borderRadius: 12,
                        background: "#f8fafc",
                        outline: "none",
                    }}
                />
            ))}
        </Box>
    );

    return (
        <Box
            component="form"
            onSubmit={handleCreate}
            sx={{
                width: { xs: "90%", sm: "420px" },
                background: "#fff",
                borderRadius: 4,
                p: 4,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            }}
        >
            <Typography variant="h5" fontWeight="bold">
                Create your TPIN
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Set a 4-digit TPIN to secure your account and enable quick login.
            </Typography>

            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151", mb: 1, textTransform: "uppercase" }}>
                New TPIN
            </Typography>
            {renderRow(newTpin, setNewTpin, "setup-new")}

            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#374151", mb: 1, textTransform: "uppercase" }}>
                Confirm TPIN
            </Typography>
            {renderRow(confirmTpin, setConfirmTpin, "setup-confirm")}

            <Button
                type="submit"
                fullWidth
                disabled={isDisabled}
                sx={{
                    height: 52,
                    background: isDisabled ? "#d1d5db" : "linear-gradient(135deg, #16a34a, #22c55e)",
                    color: isDisabled ? "#888" : "#fff",
                    borderRadius: 3,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 15,
                }}
            >
                {saving ? "Saving..." : "Set TPIN"}
            </Button>

            {onBack && (
                <>
                    <Box sx={{ height: "1px", background: "#e0e0e0", my: 2 }} />
                    <Typography
                        fontSize={14}
                        sx={{ color: "#64748b", cursor: "pointer", fontWeight: 500 }}
                        onClick={onBack}
                    >
                        ← Back
                    </Typography>
                </>
            )}
        </Box>
    );
}